import React, { Component } from "react";
import propTypes from "prop-types";
import { observer, inject } from "mobx-react";
import withRouter from "../../components/withRouter";
import { Button } from "react-bootstrap";
import { Button as AndtButton } from "antd";
import { Checkbox } from "../../components/formComponents";
import t from "../../service/translate.service";
import moment from "moment";
import LoadingPageOverlay from "../../components/LoadingPageOverlay";
import { Alert, Modal } from "antd";
import {
  extractError,
  extractErrorFromSap,
} from "../../service/response.service";
import rentContractDocumentStore from "../../store/rentContractDocuments.store";
import { saveAs } from "file-saver";
import CertificateAlerts from "../../components/CertificateAlerts";
import ContractVerificationPage from "./ContractVerificationPage";
import DOMPurify from "dompurify";
import { appFeatureEnabled } from "../../service/appFeatures.service";

class ContractOverview extends Component {
  static propTypes = {
    contractStore: propTypes.object.isRequired,
    consumptionStore: propTypes.object.isRequired
  };
  constructor(props) {
    super(props);
    this.state = {
      contract: "",
      errorMsg: "",
      loading: true,
      isShowModal: false,
      modalContent: null,
      contractVerified: false,
      verificationStarted: false,
      verificationEnabled: false,
    };
  }

  async componentDidMount() {
    this.props.contractStore.clearError()
    await this?.props?.contractStore?.initContracts()
    const contractNumber = this?.props?.contractStore?.selectedContract?.contractNumber

    try {
      const id = this.props.useParams.contractNumber;
      const selectedContract = this?.props?.contractStore?.activeContracts?.find(
        (c) => c.contractNumber === id
      );
      if (!selectedContract?.rentAccountBalance) {
        await this?.props?.contractStore?.fetchAccountBalance(selectedContract);
      }
      this.setState({ contract: selectedContract });
      this.contract = selectedContract;
      if(appFeatureEnabled('verificationEnabled')) {
        const res = await this?.props?.contractStore?.getContractVerificationInfo(
          contractNumber
        );
        if(res) this.setState({contractVerified: true})
        this.props.contractStore.getContractDetails(
          contractNumber
        );
      } else this.props.contractStore.getContractDetails(contractNumber)
      const overdueToApp =
        await this.props.consumptionStore.getConsumptionRegistrationInfo(
          contractNumber,
          "Overdue"
        );
      this.setState({ overdueToApp });
      await this.props.contractStore.getCertificates(id);
    } catch (err) {
      this.setState({
        loading: false,
        errorMsg:
          this.props.contractStore.error ||
          extractErrorFromSap(err) ||
          t[extractError(err)] ||
          t["ERROR"],
      });
    } finally {
      this.setState({ loading: false });
    }
  }

  optIn = async () => {
    try {
      this.setState({
        loading: true,
        overdueToApp: !this.state.overdueToApp,
      });

      await this.props.consumptionStore.consumptionRegister(
        this.state.contract,
        !this.state.overdueToApp,
        "Overdue"
      );

      const response =
        await this.props.consumptionStore.getConsumptionRegistrationInfo(
          this.state.contract.contractNumber,
          "Overdue"
        );

      this.setState({ overdueToApp: response });
    } catch (err) {
      this.setState({
        loading: false,
        errorMsg:
          this.props.contractStore.error ||
          extractErrorFromSap(err) ||
          t[extractError(err)] ||
          t["ERROR"],
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  showModal = () => {
    this.modal.visible = true;
  };

  openBalanceHistory = (contractNumber) => {
    this.props.navigate(`/rent-account/${contractNumber}`);
  };

  openPrepayments = (contractNumber) => {
    this.props.navigate(`/contracts/prepayments/${contractNumber}`)
  }

  createCertificateRequest = async (contractNumber, type) => {
    const certificate = await this.props.contractStore.createCertificate(contractNumber, type)

    const translation = this.props.contractStore.error.includes('ZTEN')
      ? this.props.contractStore.error
      : this.props.contractStore.error
        ? "CERTIFICATE_RENT_MODAL_ERROR"
        : `CERTIFICATE_RENT_${certificate.type.toUpperCase()}_MODAL`

    const text = this.props.contractStore.error.includes('ZTEN')
      ? (
        <>
          {t[`${translation}_TEXT_1`]}
          <a
            style={{
              color: 'var(--brand-dark-grey)',
              borderBottom: '1px solid var(--brand-green)',
              textDecoration: 'none'
            }}
            href={DOMPurify.sanitize(
              `/repairs-and-inquiries/new-request?contractNumber=${contractNumber}`
            )}
          >
            {t[`${translation}_TEXT_2`]}{" "}
          </a>
          {t[`${translation}_TEXT_3`]}
        </>
      )
      : t[`${translation}_TEXT`]

    this.setState({
      isShowModal: true,
      modalContent: (
        <>
          <h2>{t[`${translation}_TITLE`]}</h2>
          <p style={{ fontWeight: '400', lineHeight: '1.5' }}>{text}</p>
        </>
      )
    })
  }

  downloadDocument = async (contractNumber, documentId, documentName) => {
    this.setState({loading: true})
    try {
      const file = await rentContractDocumentStore.getDocument(
        contractNumber,
        documentId,
        documentName
      );
      saveAs(file, documentName, {});
      this.setState({loading: false})
    } catch (err) {
      this.setState({loading: false, errorMsg: t['CERTIFICATE_RENT_DOWNLOAD_ERROR']})
      window.scrollTo(0, 0);
    }
  };

  startVerificationProcess = () => {
    this.setState({verificationStarted: true})
  }

  render() {
    const { loading, overdueToApp, contract, errorMsg } = this.state;
    const contractNumber = this?.props?.contractStore?.selectedContract?.contractNumber || ''

    if (loading || this.props.contractStore.loading) {
      return <LoadingPageOverlay />;
    }

    const {
      contractStartDate,
      contractEndDate,
      coldCosts,
      operationalCosts,
      heatingCosts,
      tenants,
      totalCost,
      contractDetailsError,
      certificates,
      operationalCostPrepaymentEnabled,
      error,
      verificationStarted,
      userVerified,
      enabledCertificates,
      paymentMethod,
      role,
      partnerId
    } = this.props.contractStore;

    if (contractDetailsError.length) {
      return (
        <Alert
          message={contractDetailsError}
          type="error"
          style={{ marginBottom: "12px", marginTop: "12px" }}
          aria-live="assertive"
          role='alert'
        />
      );
    }

    return (
      <>
        {this.state.isShowModal && (
          <Modal
            title={this.state.modalContent}
            open={true}
            closable={false}
            onOk={() => this.setState({ isShowModal: false })}
            style={{ marginBottom: "12px", marginTop: "12px" }}
            cancelButtonProps={{ style: { display: 'none' } }}
            role="dialog"
            aria-labelledby="modalTitle"
          />
        )}

        <CertificateAlerts
          certificates={enabledCertificates}
          contractVerified={this.state.contractVerified}
          contract={{...this.contract, role}}
        />

        {error && !error.includes("ZTEN") && (
          <Alert
            message={error}
            type="error"
            style={{ marginBottom: "12px", marginTop: "12px" }}
            role="alert"
            aria-live="assertive"
          />
        )}

        {this.state.verificationStarted && (
          <ContractVerificationPage
            isAddingContract={false}
            redirectUrl={`/contract-overview/${this.contract.contractNumber}`}
          />
        )}

        <div className="contract-list">
          {errorMsg && (
            <Alert
              message={errorMsg}
              type="error"
              style={{ marginBottom: "12px", marginTop: "12px" }}
              role="alert"
              aria-live="assertive"
            />
          )}
          <div>
            {!this.state.contractVerified &&
              appFeatureEnabled('verificationEnabled') && (
                <AndtButton
                  onClick={() => {
                    this.startVerificationProcess();
                    this.props.contractStore.clearError();
                  }}
                  className="contractVerificationStatus"
                  aria-label={t.CONTRACT_VERIFICATION_BTN}
                >
                  {t.CONTRACT_VERIFICATION_BTN}
                </AndtButton>
              )}

            <table className="custom-table">
              <thead>
                <tr className="contractOverviewth">
                  <th className="contractOverviewtd" style={{ width: "300px" }}>
                    {t.CONTRACT_OVERVIEW_DETAILS}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="contractOverviewtd">
                    {t.LOGIN_PAGE_TENANT_NUMBER}:
                  </td>
                  <td className="contractOverviewtd">
                    {contractNumber.replace('-', '/')}
                  </td>
                </tr>
                <tr>
                  <td className="contractOverviewtd">
                    {t.RENT_ACCOUNT_START_DATE}:
                  </td>
                  <td className="contractOverviewtd">
                    {moment(contractStartDate).isValid()
                      ? moment(contractStartDate).format("DD.MM.YYYY")
                      : ""}
                  </td>
                </tr>
                {contractEndDate?.split("-")[0] !== "9999" &&
                  moment(contractEndDate).isValid() && (
                    <tr>
                      <td className="contractOverviewtd">
                        {t.RENT_ACCOUNT_END_DATE}:
                      </td>
                      <td className="contractOverviewtd">
                        {moment(contractEndDate).format("DD.MM.YYYY")}
                      </td>
                    </tr>
                  )}
                <tr>
                  <td className="contractOverviewtd">{t.RENT_PAYMENT_METHOD}:</td>
                  <td className="contractOverviewtd">
                    {paymentMethod === 'selfPayment' ? t.SELF_PAYMENT : t.DIRECT_DEBIT}
                  </td>
                </tr>
                <tr>
                  <td className="contractOverviewtd">{t.USER_ADDRESS}:</td>
                  <td className="contractOverviewtd">
                    {this?.contract?.address}
                  </td>
                </tr>
                <tr>
                  <td className="contractOverviewtd">{t.TENANTS}:</td>
                  <td className="contractOverviewtd">
                    {tenants &&
                      tenants.map((tenant, index) => (
                        <p
                          key={index}
                          className="contractOverviewtd"
                          style={{ paddingBottom: 0, marginBottom: "8px" }}
                        >
                          {tenant.firstName} {tenant.lastName}
                        </p>
                      ))}
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="custom-table m-t-30">
              <thead>
                <tr className="contractOverviewth">
                  <th className="contractOverviewtd" style={{ width: "300px" }}>
                    {t.MY_RENT}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="contractOverviewtd">{t.COLD_RENT}:</td>
                  <td className="contractOverviewtd">{coldCosts}</td>
                </tr>
                <tr>
                  <td className="contractOverviewtd">
                    {t.OPERATIONAL_COSTS}:
                  </td>
                  <td className="contractOverviewtd">{operationalCosts}</td>
                </tr>
                <tr>
                  <td className="contractOverviewtd">{t.HEATING_COSTS}:</td>
                  <td className="contractOverviewtd">{heatingCosts}</td>
                </tr>
                <tr>
                  <td className="contractOverviewtd">{t.TOTAL_COST}:</td>
                  <td className="contractOverviewtd">{totalCost}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 40 }} className="loyalty-upload">
            {appFeatureEnabled("pageRentAccountBalanceVisibleTo", [{...this.contract, role}]) && (
              <div className="text-center">
                <button
                  className="button primary"
                  onClick={() => this.openBalanceHistory(contractNumber)}
                  aria-label={t.CONTRACT_RENT_ACCOUNT_BALANCE}
                >
                  {`${t.CONTRACT_RENT_ACCOUNT_BALANCE}`}
                </button>
            </div>
            )}
            {appFeatureEnabled("rentContractDocumentsEnabled", [this.contract]) && appFeatureEnabled("pageRentContractDocumentsVisibleTo", [{...this.contract, role}]) && (
              <div className="text-center">
                <button
                  onClick={() =>
                    this.props.navigate(`/rent-contract-documents/${contractNumber}`)
                  }
                  className="button primary"
                  aria-label={t.RENT_CONTRACT_DOCUMENTS}
                >
                  {t.RENT_CONTRACT_DOCUMENTS}
                </button>
              </div>
            )}
            {appFeatureEnabled("changeRentPaymentMethodEnabled") && appFeatureEnabled("pageChangeRentPaymentMethodVisibleTo", [{...this.contract, role}]) && (
              <div className="text-center">
                <button
                  onClick={() =>
                    this.props.navigate(
                      `/my-payment-details/${contractNumber}`
                    )}
                  className="button primary"
                >
                  {t.MY_PAYMENT_DETAILS}
                </button>
              </div>
            )}
            {appFeatureEnabled("ocCalculationsEnabled") && appFeatureEnabled("pageOcCalculationsVisibleTo", [{...this.contract, role}]) && (
              <div className="text-center">
                <button
                  onClick={() =>
                    this.props.navigate(`/oc-calculations/${contractNumber}`)
                  }
                  className="button primary"
                  aria-label={t.VIEW_OC_CALCULATION}
                >
                  {t.VIEW_OC_CALCULATION}
                </button>
              </div>
            )}
            {appFeatureEnabled("operationalCostPrepaymentEnabled") && appFeatureEnabled("pageOperationalCostPrepaymentVisibleTo", [{...this.contract, role}]) && (
              <div className="text-center">
                  <button
                  className="button primary"
                  onClick={() => this.openPrepayments(contractNumber)}
                  aria-label={t.PREPAYMENT_OVERVIEW_BUTTON}
                >
                  {t.PREPAYMENT_OVERVIEW_BUTTON}
                </button>
              </div>
            )}
            {enabledCertificates.map(certificate => {
              const { isDisabled, isDownload, cooldown, type, documentId, documentName, requestedAt } = certificate
              const text = t[`CERTIFICATE_RENT_${type.toUpperCase()}_BTN`]
              return (
                <button
                  key={type}
                  style={{ position: "relative" }}
                  className="button primary"
                  onClick={() =>
                    isDownload
                      ? this.downloadDocument(contractNumber, documentId, documentName)
                      : this.createCertificateRequest(contractNumber, type)
                  }
                  disabled={isDisabled}
                  aria-label={text}
                >
                  <>
                    {isDownload ? t[`CERTIFICATE_RENT_${type}_DOWNLOAD_BTN`] : text}
                    {requestedAt && cooldown && <span
                      style={{
                        position: 'absolute',
                        top: '105%',
                        left: 0,
                        width: '100%',
                        fontSize: '.75rem',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        color: '#292929',
                      }}
                    >
                      {`${[
                        t['CERTIFICATE_RENT_RPC_COOLDOWN_1'],
                        cooldown,
                        t['CERTIFICATE_RENT_RPC_COOLDOWN_2']
                      ].join(' ')}`}
                    </span>}
                  </>
                </button>
              )
            })}
            {appFeatureEnabled("pageRentDueOptInVisibleTo", [{...this.contract, role}]) && (
              <Checkbox
                label={t.CONTARCT_RENT_DUE_OPT_IN}
                value={overdueToApp}
                onChange={this.optIn}
              />
            )}
          </div>
        </div>
      </>
    );
  }
}

export default inject(
  "contractStore",
  "consumptionStore"
)(withRouter(observer(ContractOverview)));
